import { createContext, useContext, useState, useEffect } from 'react';
import { IMsReferenceService, IKeyItem, ISubKeyItem, IRegion } from './msReference';
import { OrgService, IOrgService, ISearchOptions, IOrgSearch, IAISearch, IDISearch } from './org';
import { AiService, IAiService } from './ai';
import { DiService, IDiService } from './di';
import { debounce } from 'src/utils';
import { TDropItem } from 'src/types';
import { useAuth } from 'src/contexts/AuthProvider';

interface IServiceMap {
  msReference: IMsReferenceService;
  org: IOrgService;
  ai: IAiService;
  di: IDiService;
}

export const ServiceContext = createContext<Partial<IServiceMap>>({});
export function useMsReference() {
  const { msReference } = useContext(ServiceContext);
  if (msReference)
    return msReference;

  throw new Error("Missing required service for app: `msReference` service");
}

export function useOrgService() {
  const { org } = useContext(ServiceContext);
  if (org)
    return org;

  throw new Error("Missing required service for app: `org` service");
}

export function useAiService() {
  const { ai } = useContext(ServiceContext);
  if (ai)
    return ai;

  throw new Error("Missing required service for app: `ai` service");
}

export function useDiService() {
  const { di } = useContext(ServiceContext);
  if (di)
    return di;

  throw new Error("Missing required service for app: `di` service");
}

export const useOrgSearch = (params: ISearchOptions) => {
  const service = useOrgService();
  const [orgs, setOrgs] = useState<IOrgSearch[] | undefined>(undefined);
  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(false);

  const hasValues = () => {
    const values = Object.values(params);
    for (const value of values) {
      if (value)
        return true;
    }
    return false;
  };

  const search = async (options: ISearchOptions) => {
    setLoading(true);
    try {
      if (hasValues()) {
        const results = await service.search(options);
        console.log(results);
        setOrgs(results);
      } else {
        setOrgs(undefined);
      }
    } catch (error: any) {
      setError(error?.message || error);
    }
    setLoading(false);
  };

  useEffect(() => {
    debounce(() => {
      search(params);
    });
  }, [params]);

  return { orgs, isLoading, error };
};

export const useAiSearch = (params: ISearchOptions) => {
  const service = useOrgService();
  const [ais, setAis] = useState<IAISearch[] | undefined>(undefined);
  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(false);

  const hasValues = () => {
    const values = Object.values(params);
    for (const value of values) {
      if (value)
        return true;
    }
    return false;
  };


  const search = async (options: ISearchOptions) => {
    setLoading(true);
    try {
      if (hasValues()) {
        const results = await service.searchAis(options);
        setAis(results);
      } else {
        setAis(undefined);
      }
    } catch (error: any) {
      setError(error?.message || error);
    }
    setLoading(false);
  };

  useEffect(() => {
    debounce(() => {
      search({ ...params, aistatus: 'all', aicategory: 'all' });
    });
  }, [params]);

  return { ais, isLoading, error };
};

export const useDiSearch = (params: ISearchOptions) => {
  const service = useOrgService();
  const [di, setDi] = useState< IDISearch[] | undefined>(undefined);
  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(false);

  const hasValues = () => {
    const values = Object.values(params);
    for (const value of values) {
      if (value)
        return true;
    }
    return false;
  };

  const search = async (options: ISearchOptions) => {
    setLoading(true);
    try {
      if (hasValues()) {
        const results = await service.searchDis(options);
        setDi(results);
      } else {
        setDi(undefined);
      }
    } catch (error: any) {
      setError(error?.message || error);
    }
    setLoading(false);
  };

  useEffect(() => {
    debounce(() => {
      search({ ...params });
    });
  }, [params]);

  return { di, isLoading, error };
};

export const useReferences = () => {
  const { isLoggedIn } = useAuth();
  const msReference = useMsReference();
  const [countries, setCountries] = useState<TDropItem[] | undefined>();
  const [subTypes, setSubTypes] = useState<ISubKeyItem[]>([]);
  const [types, setTypes] = useState<IKeyItem[]>([]);
  const [regions, setRegions] = useState<IRegion[]>([]);
  const [schoolSubTypes, setSchoolSubTypes] = useState<IKeyItem[]>([]);
  const [schoolLevels, setSchoolLevels] = useState<IKeyItem[]>([]);
  const [instLevels, setInstLevels] = useState<IKeyItem[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);

  const retrieveTypes = async () => {
    setLoading(true);
    Promise.all([msReference.getSubTypes(), msReference.getTypes(), msReference.getRegions(),
      msReference.getSchoolSubTypes(), msReference.getSchoolLevels(),msReference.getInstLevels(), msReference.getCountries()]).then(result => {
      setSubTypes(result[0]);
      setTypes(result[1]);
      setRegions(result[2]);
      setSchoolSubTypes(result[3]);
      setSchoolLevels(result[4]);
      setInstLevels(result[5]);
      setCountries(result[6]);
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (isLoggedIn) {
      retrieveTypes();
    }
  }, [isLoggedIn]);

  return { types, subTypes, regions, schoolSubTypes, schoolLevels, instLevels, countries, isLoading };
};

export interface IServiceProvider extends IServiceMap {
  children: any;
}

export const ServiceProvider = ({ children, ...rest }: IServiceProvider) => {
  const Provider = ServiceContext.Provider;
  return <Provider value={rest}>
    {children}
  </Provider>;
};

export { MsReferenceService } from './msReference';
export { OrgService };
export { AiService };
export { DiService };